import { normalizeName } from "../parser/item-input.js";
import type { Uuid } from "../types/base.js";
import type { Product, TripItem } from "../types/entities.js";
import type { Receipt, ReceiptPosition } from "../ai/schema.js";

export interface MatchedPosition {
  position: ReceiptPosition;
  /** Position im Einkauf, falls eine passt. */
  tripItemId: Uuid | null;
  productId: Uuid | null;
  /** Preis dieser Position in Cent, aus Gesamt- oder Einzelpreis. */
  priceCents: number | null;
  /** 0 bis 1. Unter 0,5 wird nichts zugeordnet. */
  score: number;
}

export interface ReceiptPlan {
  matched: MatchedPosition[];
  /** Summe des Bons, notfalls aus den Positionen gerechnet. */
  totalCents: number | null;
  /** Positionen, zu denen kein Artikel im Einkauf passt. */
  unmatched: number;
}

/**
 * Wörter einer Bonzeile. Zahlen, Maße und die üblichen Füllsel fliegen raus:
 * "H-Milch 3,5% 1L" soll auf "Milch" passen, nicht auf "1".
 */
const FUELLWOERTER = new Set(["bio", "frisch", "gut", "billig", "aktion", "stk", "kg", "g", "ml", "l", "pkg", "beutel", "dose", "glas", "flasche", "packung"]);

function woerter(raw: string): string[] {
  return normalizeName(raw)
    .split(/[\s.\-%]+/u)
    .filter((wort) => wort.length >= 3 && !/^\d/u.test(wort) && !FUELLWOERTER.has(wort));
}

/**
 * Wie gut zwei Namen zueinander passen, 0 bis 1.
 *
 * Kein Levenshtein, sondern gemeinsame Wörter mit Rücksicht auf
 * Zusammensetzungen: Im Deutschen heißt dieselbe Sache auf dem Bon
 * "H-Milch" und auf der Liste "Milch", und ein Wort, das im anderen steckt,
 * ist ein Treffer. Genau das leistet Levenshtein hier nicht, weil die
 * Zeichenabstände bei Komposita groß sind.
 */
export function nameScore(a: string, b: string): number {
  const links = woerter(a);
  const rechts = woerter(b);
  if (links.length === 0 || rechts.length === 0) return 0;

  let treffer = 0;
  for (const wort of links) {
    const passt = rechts.some((anderes) => anderes === wort || anderes.includes(wort) || wort.includes(anderes));
    if (passt) treffer += 1;
  }

  // Am kürzeren Namen gemessen: "Milch" gegen "H-Milch 3,5% frisch" soll ein
  // voller Treffer sein und nicht an der Länge der Bonzeile scheitern.
  return treffer / Math.min(links.length, rechts.length);
}

function preisVon(position: ReceiptPosition): number | null {
  if (position.price_cents !== null) return position.price_cents;
  if (position.unit_price_cents === null) return null;
  const menge = position.qty ?? 1;
  return Math.round(position.unit_price_cents * menge);
}

/**
 * Ordnet die Positionen eines Bons den Artikeln eines Einkaufs zu.
 *
 * Jede Position bekommt höchstens einen Artikel und jeder Artikel höchstens
 * eine Position: Auf einem Bon steht Milch auch mal zweimal, und dann soll
 * der zweite Eintrag nicht denselben Artikel noch einmal überschreiben. Die
 * besten Paare zuerst, damit eine schwache Übereinstimmung keine starke
 * wegnimmt.
 */
export function planReceipt(input: {
  receipt: Receipt;
  tripItems: readonly TripItem[];
  products: readonly Product[];
}): ReceiptPlan {
  const { receipt, tripItems, products } = input;
  const offen = tripItems.filter((eintrag) => eintrag.deleted_at === null);

  // Alle Paarungen bewerten, dann absteigend abarbeiten.
  const paare: { posIndex: number; item: TripItem; score: number }[] = [];
  receipt.positions.forEach((position, posIndex) => {
    for (const item of offen) {
      const score = nameScore(position.name, item.name);
      if (score >= 0.5) paare.push({ posIndex, item, score });
    }
  });
  paare.sort((a, b) => b.score - a.score);

  const zuPosition = new Map<number, { item: TripItem; score: number }>();
  const vergebeneItems = new Set<string>();
  for (const paar of paare) {
    if (zuPosition.has(paar.posIndex) || vergebeneItems.has(paar.item.id)) continue;
    zuPosition.set(paar.posIndex, { item: paar.item, score: paar.score });
    vergebeneItems.add(paar.item.id);
  }

  const lebendeProdukte = products.filter((product) => product.deleted_at === null);

  const matched: MatchedPosition[] = receipt.positions.map((position, posIndex) => {
    const treffer = zuPosition.get(posIndex);
    // Ohne Artikel im Einkauf noch ein Blick in den Katalog: Ein Preis für ein
    // bekanntes Produkt ist auch dann wertvoll, wenn der Artikel diesmal nicht
    // auf der Liste stand.
    const ausKatalog =
      treffer?.item.product_id ??
      lebendeProdukte
        .map((product) => ({ product, score: nameScore(position.name, product.name) }))
        .filter((eintrag) => eintrag.score >= 0.5)
        .sort((a, b) => b.score - a.score || b.product.use_count - a.product.use_count)[0]?.product.id ??
      null;

    return {
      position,
      tripItemId: treffer?.item.id ?? null,
      productId: ausKatalog,
      priceCents: preisVon(position),
      score: treffer?.score ?? 0,
    };
  });

  const summeAusPositionen = matched.reduce((summe, eintrag) => summe + (eintrag.priceCents ?? 0), 0);

  return {
    matched,
    totalCents: receipt.total_cents ?? (summeAusPositionen > 0 ? summeAusPositionen : null),
    unmatched: matched.filter((eintrag) => eintrag.tripItemId === null).length,
  };
}
